/**
 * Request-scoped visitor IP context.
 *
 * `openingsFetch` needs to know the current request's visitor IP so it can
 * send it to Openings as `x-openings-client-ip` (see the same-origin proxy
 * at `src/app/api/openings/[...path]/route.ts` for the header contract).
 * Threading an `ip` parameter through every `EditionJson` method would touch
 * the 52KB `openingsLocations.ts` and every call site for a value only one
 * function consumes — so instead the request handler wraps its work in
 * `runWithVisitorIp`, and `openingsFetch` reads it back with `getVisitorIp`.
 *
 * `AsyncLocalStorage` (not a module-level variable) is what keeps concurrent
 * requests isolated from each other across `await` boundaries — Node
 * threads the active store through the async call graph per-invocation,
 * so two overlapping `runWithVisitorIp` calls never see each other's IP.
 *
 * Outside any `runWithVisitorIp` call — static generation, build-time
 * calls, anything not on a request path — `getVisitorIp()` returns
 * `undefined` rather than throwing or returning a stale value.
 *
 * ## Why `globalThis.AsyncLocalStorage` and not `import "node:async_hooks"`
 *
 * **This module must stay browser-safe.** Its only consumer is
 * `openingsFetch` in `src/lib/api/openingsClient.ts`, which is deliberately
 * isomorphic (`getOpeningsApiBase()` has a browser branch) and is reached
 * from client components — `src/app/photos/hooks/usePhotoLikes.ts`,
 * `.../MemberOpenings/index.tsx`, and transitively via `openingsLocations.ts`
 * → `src/lib/api/index.ts` → `AppointmentNote/index.tsx`. A static
 * `import { AsyncLocalStorage } from "node:async_hooks"` here therefore
 * enters the *browser* chunk's module graph and fails `next build` outright
 * — "the chunking context (unknown) does not support external modules
 * (request: node:async_hooks)" — taking down the whole site, not just the
 * mobile BFF. This is pinned by a test in `__tests__/visitorContext.test.ts`,
 * because lint, typecheck and the unit suite all stay green through that
 * regression; only `yarn build` catches it otherwise.
 *
 * Reading the constructor off `globalThis` is exactly what Next itself does
 * (`next/dist/server/app-render/async-local-storage.js`), and it is safe
 * because `next/dist/server/node-environment-baseline.js` populates
 * `globalThis.AsyncLocalStorage` before any app code runs in the Node
 * runtime. The Edge runtime provides it as a global too.
 *
 * When the global is absent — the browser, or a bare Node process such as a
 * test runner that has not loaded Next's baseline — this degrades to a no-op
 * store: `run` still invokes `fn`, and `getVisitorIp()` returns `undefined`,
 * so `visitorProxyHeaders()` sends nothing and behavior is exactly what it
 * was before this mechanism existed. That is the same safe no-op as an unset
 * `OPENINGS_PROXY_SECRET` — never a stale or cross-request IP.
 *
 * Vitest does **not** load Next's baseline, so the degraded path would
 * silently blank out this module's own tests. `vitest.setup.ts` populates
 * `globalThis.AsyncLocalStorage` from `node:async_hooks` instead (wired via
 * `setupFiles` in `vitest.config.ts`), which keeps the tests exercising the
 * real `AsyncLocalStorage` semantics they are written to prove.
 */
type VisitorIpStorage = {
  getStore(): string | undefined;
  run<T>(store: string | undefined, fn: () => Promise<T>): Promise<T>;
};

const AsyncLocalStorageCtor = (
  globalThis as {
    AsyncLocalStorage?: new () => VisitorIpStorage;
  }
).AsyncLocalStorage;

const visitorIpStorage: VisitorIpStorage = AsyncLocalStorageCtor
  ? new AsyncLocalStorageCtor()
  : { getStore: () => undefined, run: (_store, fn) => fn() };

export const runWithVisitorIp = <T>(
  ip: string | undefined,
  fn: () => Promise<T>,
): Promise<T> => visitorIpStorage.run(ip, fn);

export const getVisitorIp = (): string | undefined => visitorIpStorage.getStore();
